import { Box, Card, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlMapShapeIcon } from "~/__generated__";
import { GaiaMapStyleAreaFillSolidLegendIcon } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleAreaFillSolid";
import { GaiaMapStyleAreaFillWithDashedLinesLegendIcon } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleAreaFillWithDashedLines";
import { GaiaMapStyleLineLegendIcon } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleLine";
import { MAP_SHAPE_ICON_FILES } from "~/modules/map/icons";

import { GaiaMapLegendRow } from "./GaiaMapLegendRow";
import { GaiaMapLegendSection } from "./GaiaMapLegendSection";

export const MAP_LEGEND_ICON_SIZE = 16;

type LegendSpecies = {
    id: string;
    name: string;
    mapColor: string;
};

type LegendPointFunctionType = {
    id: string;
    name: string;
    mapIcon: GqlMapShapeIcon;
};

type LegendLineFunctionType = {
    id: string;
    name: string;
};

type LegendPolygonFunctionType = {
    id: string;
    name: string;
};

type Props = {
    species: LegendSpecies[];
    pointFunctionTypes: LegendPointFunctionType[];
    lineFunctionTypes: LegendLineFunctionType[];
    polygonFunctionTypes: LegendPolygonFunctionType[];
};

export function ProjectDeliverablesMapLegend({ species, pointFunctionTypes, lineFunctionTypes, polygonFunctionTypes }: Props) {
    const { t } = useTranslation("project");

    return (
        <Card
            sx={{
                position: "absolute",
                left: 8,
                bottom: 8,
            }}
            component={Stack}
            padding={2}
            gap={2}
        >
            <Typography
                variant="body2"
                sx={theme => ({ textTransform: "uppercase", color: theme.palette.primary.main, fontWeight: "bold" })}
            >
                {t("deliverablesScreen.map.legend.title")}
            </Typography>

            <GaiaMapLegendSection title={t("deliverablesScreen.map.legend.species")}>
                {species.map(item => (
                    <GaiaMapLegendRow
                        key={item.id}
                        icon={
                            <Box
                                sx={{
                                    width: MAP_LEGEND_ICON_SIZE,
                                    height: MAP_LEGEND_ICON_SIZE,
                                    borderRadius: "50%",
                                    bgcolor: item.mapColor,
                                    flexShrink: 0,
                                }}
                            />
                        }
                        label={item.name}
                    />
                ))}
            </GaiaMapLegendSection>

            <Divider />

            <GaiaMapLegendSection title={t("deliverablesScreen.map.legend.functions")}>
                {pointFunctionTypes.map(pointFunctionType => (
                    <GaiaMapLegendRow
                        key={pointFunctionType.id}
                        icon={
                            <Box
                                component="img"
                                src={MAP_SHAPE_ICON_FILES[pointFunctionType.mapIcon]}
                                sx={{ width: MAP_LEGEND_ICON_SIZE, height: MAP_LEGEND_ICON_SIZE, flexShrink: 0 }}
                            />
                        }
                        label={pointFunctionType.name}
                    />
                ))}
                {lineFunctionTypes.map(item => (
                    <GaiaMapLegendRow key={item.id} icon={<GaiaMapStyleLineLegendIcon />} label={item.name} />
                ))}
                {polygonFunctionTypes.map(item => (
                    <GaiaMapLegendRow key={item.id} icon={<GaiaMapStyleAreaFillWithDashedLinesLegendIcon />} label={item.name} />
                ))}
            </GaiaMapLegendSection>

            <Divider />

            <GaiaMapLegendSection title={t("deliverablesScreen.map.legend.areas")}>
                <GaiaMapLegendRow
                    icon={<GaiaMapStyleAreaFillSolidLegendIcon />}
                    label={t("deliverablesScreen.map.legend.plannerArea")}
                />
            </GaiaMapLegendSection>
        </Card>
    );
}
